import {
  TierAuditDiff,
  TierAuditImageAction,
  TierAuditLevelChange,
  TierAuditReviewTrigger,
} from "../ShopTierBenefit/tier-audit-diff";

// Wording started from describeProgramDiff / describeLevelChange in
// sellcoda-back-sellcoda-crm customer-tier.service.ts; rebateTitle and rebateDetail have since
// been reworded here, so those two lines no longer match the Excel export.

export interface AuditLine {
  text: string;
  /** true = a detail line under a tier heading */
  sub?: boolean;
}

function parseJson<T>(value: string | null | undefined): T | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === "object" && parsed !== null ? (parsed as T) : null;
  } catch {
    return null;
  }
}

const orDash = (v: unknown) => (v === null || v === undefined ? "-" : String(v));

const imageText = (label: string, action: TierAuditImageAction) =>
  action === "cleared" ? `ล้าง${label}` : `เปลี่ยน${label}`;

const triggerText = (t: TierAuditReviewTrigger) =>
  `${t?.mode ?? "-"} ${orDash(t?.value)}%` + (t?.isNotAutoDemotable ? " (ห้ามลดอัตโนมัติ)" : "");

function describeLevelChange(change: TierAuditLevelChange): string | null {
  switch (change?.type) {
    case "benefitAdd":
      return `เพิ่ม "${change.name}"`;
    case "benefitRemove":
      return `เอาออก "${change.name}"`;
    case "color":
      return change.value ? `เปลี่ยนสีของระดับเป็น ${change.value}` : "ล้างสีของระดับ";
    case "tierImageUrl":
      return imageText("รูปการ์ดระดับ", change.action);
    case "tierBgImageUrl":
      return imageText("พื้นหลังของระดับ", change.action);
    case "rebateImageUrl":
      return imageText("รูป Rebate", change.action);
    case "rebatePercent":
      return `% Rebate ${orDash(change.from)}% -> ${orDash(change.to)}%`;
    case "rebateTitle":
      return `แก้ ${orDash(change.from)} -> ${orDash(change.to)}(ชื่อสิทธิ์ Rebate)`;
    case "rebateDetail":
      return "แก้ Rebate (คำอธิบาย)";
    case "reviewTrigger":
      return `เงื่อนไขการพิจารณา: ${triggerText(change.from)} -> ${triggerText(change.to)}`;
    case "conditionExpression":
      return "เปลี่ยนเงื่อนไขระดับ";
    default:
      return null;
  }
}

/**
 * "ก่อน" of an UPDATE_PROGRAM row: benefit counts before the save for each tier that changed.
 * Program-level changes (diff.program) are intentionally not shown.
 * Returns null when valueAfter is not a TierAuditDiff (caller shows the raw string).
 */
export function describeAuditBefore(valueAfter: string | null): AuditLine[] | null {
  const diff = parseJson<TierAuditDiff>(valueAfter);
  if (!diff || !Array.isArray(diff.levels)) return null;
  return diff.levels.map((level) => ({
    text: `${level.tierName} ${Number(level.benefitCountBefore) || 0} รายการ`,
  }));
}

/**
 * "หลัง" of an UPDATE_PROGRAM row: each changed tier with its new benefit count and every
 * change under it. Program-level changes (diff.program) are intentionally not shown.
 * Unknown change types are skipped.
 * Returns null when valueAfter is not a TierAuditDiff (caller shows the raw string).
 */
export function describeAuditAfter(valueAfter: string | null): AuditLine[] | null {
  const diff = parseJson<TierAuditDiff>(valueAfter);
  if (!diff || !Array.isArray(diff.levels)) return null;

  const lines: AuditLine[] = [];
  for (const level of diff.levels) {
    lines.push({ text: `${level.tierName} ${Number(level.benefitCountAfter) || 0} รายการ` });
    for (const change of level.changes ?? []) {
      const text = describeLevelChange(change);
      if (text) lines.push({ text, sub: true });
    }
  }
  return lines;
}

export interface AuditLevelDetail {
  /** e.g. "Basic · 0 -> 2 รายการ" */
  heading: string;
  changes: string[];
}

/**
 * Full per-tier breakdown of an UPDATE_PROGRAM row for the audit detail page.
 * Program-level changes (diff.program) are intentionally not shown, as in the table.
 * Returns null when valueAfter is not a TierAuditDiff.
 */
export function describeAuditLevels(valueAfter: string | null): AuditLevelDetail[] | null {
  const diff = parseJson<TierAuditDiff>(valueAfter);
  if (!diff || !Array.isArray(diff.levels)) return null;
  return diff.levels.map((level) => ({
    heading: `${level.tierName} · ${Number(level.benefitCountBefore) || 0} -> ${
      Number(level.benefitCountAfter) || 0
    } รายการ`,
    changes: (level.changes ?? [])
      .map((change) => describeLevelChange(change))
      .filter((text): text is string => text !== null),
  }));
}

/** Names of the tiers that have at least one change in an UPDATE_PROGRAM valueAfter. */
export function changedTierNames(valueAfter: string | null): string[] {
  const diff = parseJson<TierAuditDiff>(valueAfter);
  if (!diff || !Array.isArray(diff.levels)) return [];
  return diff.levels.filter((level) => (level.changes ?? []).length > 0).map((level) => level.tierName);
}
